import { useCallback, useEffect, useState } from 'react';
import { Check, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import CoTActivityCard from './CoTActivityCard';
import AgentProgressTimeline from './AgentProgressTimeline';
import CoTSourceChip from './CoTSourceChip';
import CoTDocumentChip from './CoTDocumentChip';
import CoTTermPill from './CoTTermPill';
import CoTErrorCard from './CoTErrorCard';
import CoTConfirmCard from './CoTConfirmCard';
import CoTClarifyCard from './CoTClarifyCard';
import CoTEditBatchCard from './CoTEditBatchCard';
import CoTAnswerBlock, { type CoTAction } from './CoTAnswerBlock';
import type { ActivityPhase, CoTStepData, CoTErrorRenderContext } from './types';
import type { DocEditRecoveryResult, DocumentVersionSummary, EditBatch, HtmlEditApplyResult } from '@corner/shared';
import { APP } from '../tokens';

export type { CoTStepData, CoTErrorRenderContext } from './types';

const F = APP.fontFamily;
/** Serif stack for agent reasoning (distinct from UI sans; matches doc-adjacent feel). */
const THINKING_SERIF = '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, "Times New Roman", serif';

const SHELL_KF = 'cot-shell-pulse-kf';
if (typeof document !== 'undefined' && !document.getElementById(SHELL_KF)) {
  const style = document.createElement('style');
  style.id = SHELL_KF;
  style.textContent = `@keyframes cotShellPulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:.72;transform:scale(.94)}}
@keyframes cotSpin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`;
  document.head.appendChild(style);
}

function CoTUnderstandingBlock({ text }: { text: string }) {
  const [open, setOpen] = useState(true);
  return (
    <div
      style={{
        paddingBottom: 12,
        marginBottom: 4,
        borderBottom: `1px solid ${APP.border}`,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          width: '100%',
          padding: 0,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: F,
        }}
      >
        <ChevronRight
          size={14}
          color={APP.textMuted}
          style={{
            flexShrink: 0,
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform 160ms ease',
          }}
        />
        <span style={{ fontSize: 12, fontWeight: 600, color: APP.textCardBody, lineHeight: '16px' }}>
          How I&apos;m approaching this
        </span>
      </button>
      {open && (
        <p
          style={{
            margin: '10px 0 0',
            paddingLeft: 20,
            fontFamily: THINKING_SERIF,
            fontSize: 13,
            lineHeight: 1.55,
            color: '#8B8178',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'anywhere',
          }}
        >
          {text.trim()}
        </p>
      )}
    </div>
  );
}

function ShellHeaderStatus({
  status,
}: {
  status: 'planning' | 'working' | 'done' | 'error';
}) {
  if (status === 'planning') {
    return (
      <Loader2
        size={15}
        color="#D97645"
        style={{ flexShrink: 0, animation: 'cotSpin 1s linear infinite' }}
      />
    );
  }
  if (status === 'error') {
    return (
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: '#D94545',
          flexShrink: 0,
        }}
      />
    );
  }
  if (status === 'done') {
    return (
      <span
        style={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          backgroundColor: '#E8F2EC',
          border: `1px solid #B8D4C4`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Check size={11} color="#2D6E5A" strokeWidth={2.5} />
      </span>
    );
  }
  return (
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: '#D97645',
        flexShrink: 0,
        animation: 'cotShellPulse 2.2s ease-in-out infinite',
      }}
    />
  );
}

interface CoTContainerProps {
  status: 'planning' | 'working' | 'done' | 'error';
  steps: CoTStepData[];
  /** Head-chef plan summary — shown as the primary reasoning block (serif, low-contrast). */
  understanding?: string;
  answer?: {
    content: string;
    actions?: CoTAction[];
  };
  activityPhases?: ActivityPhase[];
  onRetryStep?: (stepIndex: number) => void;
  onSkipStep?: (stepIndex: number) => void;
  onConfirmStep?: (stepIndex: number) => void;
  onCancelStep?: (stepIndex: number) => void;
  onClarifyAnswer?: (stepIndex: number, answer: string) => void;
  onClarifyCancel?: (stepIndex: number) => void;
  onEditBatchChange?: (batch: EditBatch) => void;
  onEditBatchVersionReady?: (batch: EditBatch, htmlRevision?: HtmlEditApplyResult) => void;
  onEditBatchRecoveryVersionReady?: (result: DocEditRecoveryResult) => void;
  documentVersions?: DocumentVersionSummary[];
  onSelectDocumentVersion?: (version: DocumentVersionSummary) => void;
  editArtifactFormat?: 'source' | 'pdf';
  getToken?: () => string | null;
  onToggleActivityPhase?: (phaseId: string) => void;
  onToggleActivityStep?: (phaseId: string, stepId: string) => void;
  errorRenderContext?: CoTErrorRenderContext;
  partialCompletion?: boolean;
  answerStreaming?: boolean;
}

export default function CoTContainer({
  status,
  steps,
  understanding,
  answer,
  activityPhases,
  onRetryStep,
  onSkipStep,
  onConfirmStep,
  onCancelStep,
  onClarifyAnswer,
  onClarifyCancel,
  onEditBatchChange,
  onEditBatchVersionReady,
  onEditBatchRecoveryVersionReady,
  documentVersions,
  onSelectDocumentVersion,
  editArtifactFormat = 'source',
  getToken,
  onToggleActivityPhase,
  onToggleActivityStep,
  errorRenderContext,
  partialCompletion,
  answerStreaming,
}: CoTContainerProps) {
  const [shellOpen, setShellOpen] = useState(true);

  useEffect(() => {
    const onFocusBatch = (event: Event) => {
      const batchId = (event as CustomEvent<{ batchId?: string }>).detail?.batchId;
      if (!batchId) return;
      setShellOpen(true);
      window.setTimeout(() => {
        const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(batchId) : batchId.replace(/"/g, '\\"');
        const el = document.querySelector(`[data-cot-edit-batch-id="${escaped}"]`);
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 0);
    };
    window.addEventListener('corner:cot-focus-edit-batch', onFocusBatch);
    return () => window.removeEventListener('corner:cot-focus-edit-batch', onFocusBatch);
  }, []);

  const doneCount = steps.filter((s) => s.status === 'done' || s.status === 'skipped').length;
  const activeIdx = steps.findIndex((s) => s.status === 'active');
  const workingStepHint =
    status === 'working' && steps.length > 0
      ? activeIdx >= 0
        ? `Step ${activeIdx + 1} of ${steps.length}`
        : `${doneCount} of ${steps.length} done`
      : null;
  const allDone = status === 'done';
  const headerLabel =
    status === 'planning' ? 'Analyzing…'
    : status === 'working' ? 'Working …'
    : status === 'done' ? 'Completed'
    : 'Couldn’t finish';

  const renderStepChildren = useCallback((step: CoTStepData, i: number) => (
    <>
      {step.sources && step.sources.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {step.sources.map((src) => (
            <CoTSourceChip key={src} source={src} />
          ))}
        </div>
      )}

      {step.documents && step.documents.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {step.documents.map((doc) => (
            <CoTDocumentChip
              key={doc.filename}
              filename={doc.filename}
              source={doc.source}
              variant={doc.variant}
            />
          ))}
        </div>
      )}

      {step.terms && step.terms.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {step.terms.map((term) => (
            <CoTTermPill key={term} term={term} />
          ))}
        </div>
      )}

      {step.status === 'error' && step.errorMessage && (
        <CoTErrorCard
          message={step.errorMessage}
          errorCode={step.errorCode}
          toolName={step.errorToolName}
          errorRenderContext={errorRenderContext}
          retryableOverride={step.retryable}
          retryCountLabel={step.retryCountLabel}
          denialMessage={step.denialMessage}
          onRetry={onRetryStep && step.retryable !== false ? () => onRetryStep(i) : undefined}
          onSkip={onSkipStep && step.skippable !== false ? () => onSkipStep(i) : undefined}
        />
      )}

      {step.status === 'confirm' && step.confirmToolName && step.confirmToolName !== 'plan_doc_edits' && (
        <CoTConfirmCard
          toolName={step.confirmToolName}
          description={step.confirmDescription ?? ''}
          permissionTier={step.confirmPermissionTier ?? 'Apply'}
          onConfirm={() => onConfirmStep?.(i)}
          onCancel={() => onCancelStep?.(i)}
        />
      )}

      {step.status === 'clarify' && step.clarificationQuestion && (
        <CoTClarifyCard
          question={step.clarificationQuestion}
          options={step.clarificationOptions}
          allowFreeInput={step.clarificationAllowFreeInput}
          onAnswer={(a) => onClarifyAnswer?.(i, a)}
          onCancel={() => onClarifyCancel?.(i)}
        />
      )}

      {step.editBatch && onEditBatchChange && (
        <div style={{ marginTop: 4, alignSelf: 'stretch', width: '100%', minWidth: 0 }}>
          <CoTEditBatchCard
            batch={step.editBatch}
            getToken={getToken}
            onBatchChange={onEditBatchChange}
            onVersionReady={onEditBatchVersionReady}
            onRecoveryVersionReady={onEditBatchRecoveryVersionReady}
            documentVersions={documentVersions}
            onSelectDocumentVersion={onSelectDocumentVersion}
            artifactFormat={editArtifactFormat}
          />
        </div>
      )}
    </>
  ), [errorRenderContext, onRetryStep, onSkipStep, onConfirmStep, onCancelStep, onClarifyAnswer, onClarifyCancel, onEditBatchChange, onEditBatchVersionReady, onEditBatchRecoveryVersionReady, documentVersions, onSelectDocumentVersion, getToken, editArtifactFormat]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignSelf: 'stretch',
        gap: 10,
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignSelf: 'stretch',
          borderRadius: 10,
          border: `1px solid ${APP.borderCard}`,
          backgroundColor: APP.bgCardBody,
          boxShadow: '0 1px 3px rgba(26, 23, 20, 0.06)',
          overflow: 'hidden',
        }}
      >
        <button
          type="button"
          onClick={() => setShellOpen((o) => !o)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
            padding: '12px 14px',
            gap: 10,
            background: shellOpen ? APP.bgContent : APP.bgCardBody,
            border: 'none',
            borderBottom: shellOpen ? `1px solid ${APP.border}` : 'none',
            cursor: 'pointer',
            fontFamily: F,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <ShellHeaderStatus status={status} />
            <span
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: status === 'error' ? '#B9382D' : APP.textDark,
                lineHeight: '18px',
                letterSpacing: status === 'working' || status === 'planning' ? '0.01em' : undefined,
              }}
            >
              {headerLabel}
            </span>
            {workingStepHint && (
              <span style={{ fontSize: 11, fontWeight: 500, color: APP.textMuted, flexShrink: 0 }}>
                {workingStepHint}
              </span>
            )}
          </div>
          {shellOpen
            ? <ChevronDown size={18} color={APP.textMuted} strokeWidth={2} style={{ flexShrink: 0, opacity: 0.7 }} />
            : <ChevronRight size={18} color={APP.textMuted} strokeWidth={2} style={{ flexShrink: 0, opacity: 0.7 }} />}
        </button>

        {shellOpen && (
          <div
            style={{
              padding: '14px 14px 16px',
              display: 'flex',
              flexDirection: 'column',
              gap: 14,
              overflowY: 'visible',
              overflowX: 'hidden',
            }}
          >
            {!!understanding?.trim() && (
              <CoTUnderstandingBlock text={understanding} />
            )}

            {activityPhases && activityPhases.length > 0 && (
              <CoTActivityCard
                phases={activityPhases}
                onTogglePhase={onToggleActivityPhase}
                onToggleStep={onToggleActivityStep}
              />
            )}

            <AgentProgressTimeline
              steps={steps}
              status={status}
              compact={allDone}
              renderStepChildren={renderStepChildren}
            />

            {answer && (status === 'done' || status === 'working') && (
              <CoTAnswerBlock
                content={answer.content}
                actions={answer.actions}
                partialCompletion={partialCompletion}
                failedStepTitles={partialCompletion ? steps.filter((s) => s.status === 'error' || s.status === 'skipped').map((s) => s.title) : undefined}
                streaming={answerStreaming}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
